import { useCallback } from 'react';
import { formatPriceAsCents } from '../../../lib/formatPriceAsCents';
import { UseUserQuery_user as User } from '../../../generated/types/UseUserQuery';
import { useUser } from '../../useUser';
import { useCart, CheckoutCart, CartVersion } from '../../useCart';
import { useAddressesQuery, RestAddress } from '../../useAddress';
import { usePlaceAffirmOrderMutation } from './usePlaceAffirmOrderMutation';

type AffirmCheckoutArgs = {
  user: User;
  cart: CheckoutCart;
  shippingAddress: RestAddress;
};

export type AffirmCheckoutResponse = {
  billing_address: {
    city: string;
    line1: string;
    line2: string;
    state: string;
    zipcode: string;
  };
  callback_id: string;
  cardholder_name: string;
  checkout_token: string;
  created: string;
  cvv: string;
  expiration: string;
  id: string;
  number: string;
};

const getAffirmCheckoutData = ({ user, cart, shippingAddress }: AffirmCheckoutArgs) => ({
  shipping: {
    name: {
      first: shippingAddress.attributes.firstName,
      last: shippingAddress.attributes.lastName
    },
    address: {
      line1: shippingAddress.attributes.addressLine1,
      line2: shippingAddress.attributes.addressLine2,
      city: shippingAddress.attributes.city,
      state: shippingAddress.attributes.state,
      zipcode: shippingAddress.attributes.postalCode,
      country: shippingAddress.attributes.countryCode
    },
    phone_number: shippingAddress.attributes.phone,
    email: user.email
  },
  items: cart.attributes.cartItems.map(cartItem => ({
    display_name: cartItem.data.attributes.productName,
    sku: cartItem.data.attributes.skuId,
    unit_price: formatPriceAsCents(cartItem.data.attributes.prices.currentPrice.price),
    qty: cartItem.data.attributes.quantity,
    item_image_url: cartItem.data.attributes.imageSrc,
    item_url: window.location.origin + cartItem.data.attributes.variantHref
  })),
  shipping_amount: formatPriceAsCents(cart.attributes.paymentSummary.totalShipping.price),
  tax_amount: formatPriceAsCents(cart.attributes.paymentSummary.totalTaxes.price),
  total: formatPriceAsCents(cart.attributes.paymentSummary.cardPaymentToday.price),
  metadata: {
    mode: 'modal'
  }
});

export const useAffirmCheckout = () => {
  const { cart } = useCart();
  const { user } = useUser();
  const { addresses } = useAddressesQuery();
  const [placeOrder] = usePlaceAffirmOrderMutation();
  const shippingAddress = addresses.find(
    address => Number(address.id) === cart.attributes.addressId
  );

  const placeAffirmOrder = useCallback((): ReturnType<typeof placeOrder> => {
    if (cart.attributes.version !== CartVersion.Checkout)
      return Promise.reject('placeAffirmOrder: cart version is not checkout');
    if (!shippingAddress) return Promise.reject('placeAffirmOrder: no shipping address selected');

    return new Promise((resolve, reject) => {
      window.affirm.checkout.open_vcn({
        checkout_data: getAffirmCheckoutData({ user, cart: cart as CheckoutCart, shippingAddress }),
        success: async (response: AffirmCheckoutResponse) => {
          try {
            const result = await placeOrder(response);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
        error: (error: Error) => {
          reject(error);
        }
      });
    });
  }, [cart, shippingAddress, user, placeOrder]);

  return { placeAffirmOrder };
};
